/**
 * Modèle d'état de la partie et journal des actions.
 *
 * Tout l'état d'un îlot tient ici : c'est ce que l'on sérialise, sauvegarde et
 * recharge. Chaque champ est en lecture seule ; les fonctions du moteur
 * renvoient un nouvel état plutôt que de modifier celui qu'on leur passe.
 */
import type {
  ChainRole,
  ChapterId,
  ConnectedUsefulness,
  Content,
  GaugeId,
  ReglageProgramme,
} from './content-schema.js';
import type { JournalEntry } from './journal.js';
import { isValidIslandCode } from './island-code.js';
import { ajouterAuJournal } from './journal.js';

/** Contenu des quatre cases de la chaîne d'information, en cours de construction. */
export interface ChainState {
  readonly acquerir: readonly string[];
  readonly traiter: readonly string[];
  readonly communiquer: readonly string[];
  readonly agir: readonly string[];
}

/**
 * Journal des actions : sert à la reprise, au débogage et — en phase 8 — à
 * l'analyse de séance. Volontairement sans horodatage : le moteur est pur et
 * déterministe, c'est à l'appelant de dater les actions s'il en a besoin.
 */
/**
 * Types d'action du journal. Liste unique : le schéma de sauvegarde
 * (`serialization.ts`) doit en couvrir exactement autant, ce qu'un test vérifie.
 */
export const ACTION_TYPES = [
  'creer_partie',
  'decouvrir_probleme',
  'classer_probleme',
  'valider_classement',
  'analyser_probleme',
  'definir_priorites',
  'placer_equipement',
  'retirer_equipement',
  'vider_chaine',
  'repondre_question_guidee',
  'regler_programme',
  'organiser_tournee',
  'revenir_a_concevoir',
  'deployer',
  'resoudre_evenement',
  'ecrire_trace',
  'aller_etape',
  'changer_chapitre',
  'repondre_questionnaire',
] as const;

export type ActionType = (typeof ACTION_TYPES)[number];

export type Action =
  | { readonly type: 'creer_partie'; readonly islandCode: string }
  | { readonly type: 'decouvrir_probleme'; readonly problemId: string }
  | { readonly type: 'classer_probleme'; readonly problemId: string; readonly categoryId: string }
  | { readonly type: 'valider_classement'; readonly corrects: readonly string[] }
  | {
      readonly type: 'analyser_probleme';
      readonly problemId: string;
      readonly reponse: ConnectedUsefulness;
    }
  | { readonly type: 'definir_priorites'; readonly priorities: readonly string[] }
  | { readonly type: 'placer_equipement'; readonly slot: ChainRole; readonly equipmentId: string }
  | { readonly type: 'retirer_equipement'; readonly slot: ChainRole; readonly equipmentId: string }
  | { readonly type: 'vider_chaine' }
  | {
      readonly type: 'repondre_question_guidee';
      readonly questionId: string;
      readonly choiceId: string;
    }
  | { readonly type: 'regler_programme'; readonly reglage: ReglageProgramme }
  | {
      readonly type: 'organiser_tournee';
      readonly jour: string;
      readonly conteneurs: readonly string[];
    }
  | { readonly type: 'revenir_a_concevoir' }
  | {
      readonly type: 'deployer';
      readonly solutionId: string | null;
      readonly cout: number;
    }
  | {
      readonly type: 'resoudre_evenement';
      readonly eventId: string;
      readonly choiceId: string | null;
    }
  | {
      readonly type: 'ecrire_trace';
      readonly chapterId: ChapterId;
      readonly fieldId: string;
      readonly valeur: string;
    }
  | { readonly type: 'aller_etape'; readonly stepIndex: number }
  | { readonly type: 'changer_chapitre'; readonly chapterId: ChapterId }
  | { readonly type: 'repondre_questionnaire'; readonly chapterId: ChapterId };

export interface GameState {
  readonly islandCode: string;
  readonly chapterId: ChapterId;
  readonly stepIndex: number;
  readonly gauges: Readonly<Record<GaugeId, number>>;
  readonly discoveredProblems: readonly string[];
  /** problemId → categoryId choisi par l'îlot. */
  readonly classification: Readonly<Record<string, string>>;
  /**
   * problemId → nombre de fois que l'îlot a fait corriger son classement.
   * C'est ce qui limite les secondes chances (cf. `chapter1.ts`).
   */
  readonly classificationAttempts: Readonly<Record<string, number>>;
  /** problemId → réponse de l'îlot à « un objet connecté peut-il aider ? ». */
  readonly connectedAnalysis: Readonly<Record<string, ConnectedUsefulness>>;
  /** Trois problèmes au plus, dans l'ordre du podium. */
  readonly priorities: readonly string[];
  readonly chain: ChainState;
  readonly deployedSolutionId: string | null;
  /**
   * Les jauges telles qu'elles étaient juste avant le déploiement.
   *
   * Sert à deux choses : le bilan « avant / après » de la fiche technique, et
   * le retour à l'étape Concevoir, qui remet la ville exactement dans l'état où
   * elle était — plus fidèle que de soustraire les effets, qu'un bornage aurait
   * pu tronquer.
   */
  readonly gaugesBeforeDeploy: Readonly<Record<GaugeId, number>> | null;
  /** Nombre de retours à l'étape Concevoir déjà utilisés (un seul est permis). */
  readonly redesignsUsed: number;
  /** questionId → choiceId, pour les questions guidées de l'étape « Comprendre ». */
  readonly guidedAnswers: Readonly<Record<string, string>>;
  /**
   * Le programme d'arrosage tel que l'îlot l'a réglé, au chapitre 3.
   *
   * `null` tant qu'il n'y a pas touché : l'écran montre alors le programme de
   * la ville, celui que le contenu décrit dans `programmes[].depart`. Garder la
   * distinction permet de dire « vous n'avez encore rien changé », ce qu'une
   * copie du départ ne permettrait pas.
   */
  readonly programme: ReglageProgramme | null;
  /**
   * Les arrêts du camion, par jour de sortie, au chapitre 4.
   *
   * Un jour absent de cette table n'a pas encore été organisé ; un jour présent
   * avec une liste vide est un choix délibéré — le camion ne sort pas. La
   * distinction compte : c'est elle qui dit si l'îlot a traité la semaine.
   */
  readonly tournees: Readonly<Record<string, readonly string[]>>;
  readonly triggeredEvents: readonly string[];
  /** eventId → choiceId retenu, pour les événements à choix. */
  readonly eventChoices: Readonly<Record<string, string>>;
  readonly writtenTrace: Readonly<Record<ChapterId, Readonly<Record<string, string>>>>;
  readonly score: Readonly<Record<ChapterId, number>>;
  readonly history: readonly Action[];
  /**
   * Journal anonyme de la séance, horodaté par l'appelant.
   *
   * Séparé de `history` à dessein : `history` sert à la reprise et au débogage
   * et reste sans horodatage, le moteur étant pur ; le journal sert à l'analyse
   * de séance et a besoin du temps réel.
   */
  readonly journal: readonly JournalEntry[];
  /** Ce que l'îlot a répondu au questionnaire de fin de chapitre. */
  readonly questionnaires: Readonly<Partial<Record<ChapterId, Questionnaire>>>;
}

/** Trois niveaux, du moins bon au meilleur. */
export const NIVEAUX_QUESTIONNAIRE = ['peu', 'moyen', 'bien'] as const;
export type NiveauQuestionnaire = (typeof NIVEAUX_QUESTIONNAIRE)[number];

export interface Questionnaire {
  readonly compris: NiveauQuestionnaire;
  readonly aime: NiveauQuestionnaire;
  /**
   * « Ce qui était difficile », en texte court.
   *
   * C'est le **seul** endroit du jeu où un élève écrit librement quelque chose
   * qui remonte à l'enseignant. La consigne à l'écran demande de ne pas y mettre
   * de prénom, et la longueur est bornée — mais rien ne peut l'empêcher
   * techniquement. Ce champ n'est jamais exporté en CSV ni affiché en projection.
   */
  readonly difficile: string;
}

/** Longueur maximale du texte libre du questionnaire. */
export const MAX_TEXTE_QUESTIONNAIRE = 200;

/* ------------------------------------------------------------------ */
/* Résultat d'une action                                               */
/* ------------------------------------------------------------------ */

/**
 * Codes de refus du moteur.
 *
 * `budget_insuffisant` est le seul qui porte un message destiné à l'élève, et
 * ce message vient du contenu. Les autres signalent un geste que l'interface
 * aurait dû empêcher : leur message est technique, à l'usage du développeur.
 */
export type EngineErrorCode =
  | 'code_ilot_invalide'
  | 'probleme_inconnu'
  | 'probleme_non_decouvert'
  | 'categorie_inconnue'
  | 'equipement_inconnu'
  | 'role_incompatible'
  | 'case_pleine'
  | 'reseau_requis_pour_superviser'
  | 'equipement_absent'
  | 'equipement_deja_place'
  | 'retrait_impossible'
  | 'question_inconnue'
  | 'choix_de_question_inconnu'
  | 'rien_a_redessiner'
  | 'plus_de_retour_possible'
  | 'trop_de_priorites'
  | 'priorite_en_double'
  | 'budget_insuffisant'
  | 'chaine_non_deployable'
  | 'evenement_inconnu'
  | 'evenement_deja_resolu'
  | 'evenement_non_eligible'
  | 'choix_requis'
  | 'choix_inconnu'
  | 'champ_de_trace_inconnu'
  | 'etape_inconnue'
  | 'sauvegarde_illisible'
  | 'sauvegarde_version_inconnue';

export interface EngineError {
  readonly code: EngineErrorCode;
  /**
   * Message en français.
   *
   * Les refus qu'un geste d'élève peut produire tirent leur texte de
   * `content.messages` : le magasin les affiche tels quels. Les autres signalent
   * un défaut de programmation, et leur message reste technique — c'est un
   * développeur qui le lira.
   */
  readonly message: string;
}

/** Branche d'échec, partagée par tous les résultats du moteur. */
export interface ActionRefusal {
  readonly ok: false;
  readonly erreur: EngineError;
}

export type ActionResult = { readonly ok: true; readonly state: GameState } | ActionRefusal;

export const refus = (code: EngineErrorCode, message: string): ActionRefusal => ({
  ok: false,
  erreur: { code, message },
});

/** Ajoute une action au journal et renvoie le nouvel état. */
export function withAction(state: GameState, action: Action): GameState {
  return { ...state, history: [...state.history, action] };
}

/* ------------------------------------------------------------------ */
/* Création de l'état initial                                          */
/* ------------------------------------------------------------------ */

export const EMPTY_CHAIN: ChainState = Object.freeze({
  acquerir: [],
  traiter: [],
  communiquer: [],
  agir: [],
});

/**
 * Construit l'état de départ d'un îlot à partir des `valeur_initiale` des jauges.
 *
 * @throws {Error} si le code d'îlot est mal formé — un code invalide ne doit
 * jamais entrer dans une sauvegarde.
 */
export function createInitialState(content: Content, islandCode: string): GameState {
  if (!isValidIslandCode(islandCode)) {
    throw new Error(`Code d'îlot mal formé : « ${islandCode} ».`);
  }

  const gauges = {} as Record<GaugeId, number>;
  for (const jauge of content.jauges) {
    gauges[jauge.id] = jauge.valeur_initiale;
  }

  const writtenTrace = {} as Record<ChapterId, Record<string, string>>;
  const score = {} as Record<ChapterId, number>;
  for (const chapitre of content.chapitres) {
    writtenTrace[chapitre.id] = {};
    score[chapitre.id] = 0;
  }

  const premierChapitre = content.chapitres[0];
  if (!premierChapitre) {
    throw new Error('Le contenu ne déclare aucun chapitre.');
  }

  return {
    islandCode,
    chapterId: premierChapitre.id,
    stepIndex: 0,
    gauges,
    discoveredProblems: [],
    classification: {},
    classificationAttempts: {},
    connectedAnalysis: {},
    priorities: [],
    chain: EMPTY_CHAIN,
    deployedSolutionId: null,
    gaugesBeforeDeploy: null,
    redesignsUsed: 0,
    guidedAnswers: {},
    programme: null,
    tournees: {},
    triggeredEvents: [],
    eventChoices: {},
    writtenTrace,
    score,
    history: [{ type: 'creer_partie', islandCode }],
    journal: [],
    questionnaires: {},
  };
}

/* ------------------------------------------------------------------ */
/* Navigation entre étapes et chapitres                                */
/* ------------------------------------------------------------------ */

/** Le chapitre courant, tel que décrit par le contenu. */
export function currentChapter(state: GameState, content: Content) {
  const chapitre = content.chapitres.find((c) => c.id === state.chapterId);
  if (!chapitre) {
    throw new Error(`Chapitre absent du contenu : « ${state.chapterId} ».`);
  }
  return chapitre;
}

/** L'étape courante du chapitre courant. */
export function currentStep(state: GameState, content: Content) {
  return currentChapter(state, content).deroule[state.stepIndex];
}

/** Va à une étape précise du chapitre courant. */
export function goToStep(state: GameState, content: Content, stepIndex: number): ActionResult {
  const chapitre = currentChapter(state, content);
  if (!Number.isInteger(stepIndex) || stepIndex < 0 || stepIndex >= chapitre.deroule.length) {
    return refus(
      'etape_inconnue',
      `Le chapitre « ${chapitre.id} » n'a pas d'étape d'indice ${stepIndex}.`,
    );
  }

  return {
    ok: true,
    state: withAction({ ...state, stepIndex }, { type: 'aller_etape', stepIndex }),
  };
}

/** Passe au chapitre demandé, en repartant de sa première étape. */
export function goToChapter(
  state: GameState,
  content: Content,
  chapterId: ChapterId,
): ActionResult {
  const chapitre = content.chapitres.find((c) => c.id === chapterId);
  if (!chapitre) {
    return refus('etape_inconnue', `Chapitre absent du contenu : « ${chapterId} ».`);
  }

  return {
    ok: true,
    state: withAction(
      { ...state, chapterId, stepIndex: 0 },
      { type: 'changer_chapitre', chapterId },
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Trace écrite                                                        */
/* ------------------------------------------------------------------ */

/**
 * Enregistre la réponse de l'îlot dans un champ libre de la trace écrite.
 * Les champs `genere_auto` sont calculés à l'affichage, pas saisis : ils sont refusés ici.
 */
export function setWrittenTraceField(
  state: GameState,
  content: Content,
  chapterId: ChapterId,
  fieldId: string,
  valeur: string,
): ActionResult {
  const chapitre = content.chapitres.find((c) => c.id === chapterId);
  const champ = chapitre?.trace_ecrite.champs.find((f) => f.id === fieldId);

  if (!champ) {
    return refus(
      'champ_de_trace_inconnu',
      `Le chapitre « ${chapterId} » n'a pas de champ de trace « ${fieldId} ».`,
    );
  }
  if (champ.type === 'genere_auto') {
    return refus(
      'champ_de_trace_inconnu',
      `Le champ « ${fieldId} » est généré automatiquement : il ne se saisit pas.`,
    );
  }

  const traceDuChapitre = { ...(state.writtenTrace[chapterId] ?? {}), [fieldId]: valeur };

  return {
    ok: true,
    state: withAction(
      { ...state, writtenTrace: { ...state.writtenTrace, [chapterId]: traceDuChapitre } },
      { type: 'ecrire_trace', chapterId, fieldId, valeur },
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Journal et questionnaire                                            */
/* ------------------------------------------------------------------ */

/** Ajoute une entrée au journal de séance. L'horodatage vient de l'appelant. */
export function journaliser(state: GameState, entree: JournalEntry): GameState {
  return { ...state, journal: ajouterAuJournal(state.journal, entree) };
}

/**
 * Enregistre le questionnaire de fin de chapitre.
 *
 * Le texte libre est tronqué : une remarque d'élève tient en une phrase, et
 * une sauvegarde a une taille maximale.
 */
export function repondreQuestionnaire(
  state: GameState,
  content: Content,
  chapterId: ChapterId,
  reponses: Questionnaire,
): ActionResult {
  if (!content.chapitres.some((c) => c.id === chapterId)) {
    return refus('etape_inconnue', `Chapitre absent du contenu : « ${chapterId} ».`);
  }

  const propres: Questionnaire = {
    compris: reponses.compris,
    aime: reponses.aime,
    difficile: reponses.difficile.trim().slice(0, MAX_TEXTE_QUESTIONNAIRE),
  };

  return {
    ok: true,
    state: withAction(
      { ...state, questionnaires: { ...state.questionnaires, [chapterId]: propres } },
      { type: 'repondre_questionnaire', chapterId },
    ),
  };
}
